import { useCallback, useId, useRef, useState } from 'react'
import { Upload, Loader2, AlertCircle } from 'lucide-react'
import { cn } from '@/utils/cn'

interface FileUploadDropzoneProps {
  onFileSelect: (file: File) => void
  isLoading?: boolean
  error?: string | null
  accept?: string
  maxSizeMB?: number
  compact?: boolean
}

function parseAcceptedExtensions(accept: string): string[] {
  return accept
    .split(',')
    .map(token => token.trim().toLowerCase())
    .filter(Boolean)
}

function isAcceptedFileType(fileName: string, accept: string): boolean {
  const extensions = parseAcceptedExtensions(accept)
  if (extensions.length === 0) return true
  const lowerName = fileName.toLowerCase()
  return extensions.some(ext => lowerName.endsWith(ext))
}

function formatAcceptedTypesLabel(accept: string): string {
  const labels = parseAcceptedExtensions(accept).map(ext =>
    ext.replace(/^\./, '').toUpperCase()
  )
  if (labels.length === 0) return ''
  if (labels.length === 1) return labels[0]
  return `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}`
}

export function FileUploadDropzone({
  onFileSelect,
  isLoading = false,
  error = null,
  accept = '.csv,.xlsx',
  maxSizeMB = 10,
  compact = false,
}: FileUploadDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const descriptionId = useId()
  const [isDragging, setIsDragging] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const displayError = validationError ?? error

  const processFile = useCallback(
    (file: File) => {
      if (isLoading) return
      if (!isAcceptedFileType(file.name, accept)) {
        setValidationError(
          `Please upload a ${formatAcceptedTypesLabel(accept)} file`
        )
        return
      }
      if (file.size > maxSizeMB * 1024 * 1024) {
        setValidationError(`Please upload a file smaller than ${maxSizeMB}MB`)
        return
      }
      setValidationError(null)
      onFileSelect(file)
    },
    [accept, isLoading, maxSizeMB, onFileSelect]
  )

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)

      const files = e.dataTransfer.files
      if (files && files.length > 0) {
        processFile(files[0])
      }
    },
    [processFile]
  )

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (files && files.length > 0) {
        processFile(files[0])
      }
      // Reset input value so the same file can be selected again
      e.target.value = ''
    },
    [processFile]
  )

  return (
    <div
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={cn(
        'relative flex flex-col items-center justify-center gap-3 p-8',
        'border-2 border-dashed rounded-[12px] transition-all',
        compact && 'flex-row gap-4 p-5',
        isDragging
          ? 'border-[#CF1F2E] bg-[#FEF2F3]'
          : 'border-[#D6DAE1] bg-[#F8F8F8] hover:border-[#9CA3AF]',
        isLoading && 'pointer-events-none opacity-70'
      )}
    >
      {/* Icon */}
      <div
        className={cn(
          'flex shrink-0 items-center justify-center w-12 h-12 rounded-full',
          compact && 'h-10 w-10 rounded-lg',
          isDragging ? 'bg-[#CF1F2E] text-white' : 'bg-[#E5E7EB] text-[#556374]'
        )}
      >
        {isLoading ? (
          <Loader2 className="h-6 w-6 animate-spin" />
        ) : (
          <Upload className="h-6 w-6" />
        )}
      </div>

      {/* Text */}
      <div className={cn('text-center', compact && 'min-w-0 text-left')}>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={isLoading}
          aria-describedby={descriptionId}
          className="cursor-pointer rounded-sm text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          Upload a file
        </button>
        <input
          ref={inputRef}
          type="file"
          aria-label="Upload a file"
          accept={accept}
          onChange={handleFileInputChange}
          className="hidden"
          disabled={isLoading}
        />
        <span className="text-[14px] text-[#556374]"> or drag and drop</span>

        {/* File type info */}
        <p
          id={descriptionId}
          className="mt-2 text-[13px] text-muted-foreground"
        >
          {formatAcceptedTypesLabel(accept)} up to {maxSizeMB}MB
        </p>

        {/* Error message */}
        {displayError && (
          <div
            role="alert"
            className="flex items-center gap-2 text-[13px] text-destructive mt-2"
          >
            <AlertCircle className="h-4 w-4" />
            <span>{displayError}</span>
          </div>
        )}
      </div>
    </div>
  )
}
